import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';

import FormRow from '../../../app/components/FormRow';
import Button from '../../../common/components/Button/Button';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  RecommendedPublisherScopeEnum,
  RecommendedPublisherScopeObj,
} from '../../../common/pokCore/validation/enums';
import {
  GetProfileDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { Option } from '../Selects/Selector';
import CompanySelector from '../Selects/CompanySelector';
import PurchaserSelector from '../Selects/PurchaserSelector';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';

export interface NewRecommendationParams {
  profileId: string;
  profileName: string;
  scopeType: RecommendedPublisherScopeEnum;
  companyId?: string;
  purchaserId?: string;
}

interface Props {
  show: boolean;
  handleClose: () => void;
  onProceed: (params: NewRecommendationParams) => void;
}

export const RecommendedPublisherAddNewModal: React.FC<Props> = props => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  const [profiles, setProfiles] = useState<GetProfileDto[]>([]);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [scopeType, setScopeType] =
    useState<RecommendedPublisherScopeEnum | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [purchaserId, setPurchaserId] = useState<string | null>(null);

  const loadData = useCallback(() => {
    pok.profiles
      .getAll()
      .then(setProfiles)
      .catch((e: ResponseError) => notifications.caughtError(e));
  }, [pok.profiles, notifications]);

  useEffect(() => {
    if (props.show) {
      loadData();
    } else {
      setProfileId(null);
      setScopeType(null);
      setCompanyId(null);
      setPurchaserId(null);
    }
  }, [props.show, loadData]);

  const scopeOpts = Object.values(RecommendedPublisherScopeEnum).map(s => ({
    value: s,
    label: t(RecommendedPublisherScopeObj[s]),
  }));
  const profileOpts = profiles.map(p => ({ value: p.id, label: p.name }));

  const onScopeChange = (value: RecommendedPublisherScopeEnum | null) => {
    setScopeType(value);
    if (value !== RecommendedPublisherScopeEnum.Purchaser) {
      setPurchaserId(null);
    }
    if (value === RecommendedPublisherScopeEnum.Global) {
      setCompanyId(null);
    }
  };

  const isValid =
    !!profileId &&
    !!scopeType &&
    (scopeType === RecommendedPublisherScopeEnum.Global ||
      (scopeType === RecommendedPublisherScopeEnum.Company && !!companyId) ||
      (scopeType === RecommendedPublisherScopeEnum.Purchaser &&
        !!companyId &&
        !!purchaserId));

  const proceed = () => {
    if (!profileId || !scopeType) {
      return;
    }
    const prof = profiles.find(p => p.id === profileId);
    props.onProceed({
      profileId,
      profileName: prof?.name || '',
      scopeType,
      companyId: companyId || undefined,
      purchaserId: purchaserId || undefined,
    });
  };

  const showCompany =
    scopeType === RecommendedPublisherScopeEnum.Company ||
    scopeType === RecommendedPublisherScopeEnum.Purchaser;

  return (
    <Modal
      show={props.show}
      onHide={props.handleClose}
      centered
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t(tk.recommendedPublisher.recommendedPublisherNew)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="d-grid gap-3">
          <FormRow controlId="profile" label={t(tk.administration.profile)}>
            <Select
              options={profileOpts}
              value={profileOpts.find(o => o.value === profileId)}
              onChange={opt => setProfileId(opt?.value || null)}
              placeholder={t(tk.component.pointOut)}
              isClearable
            />
          </FormRow>
          <FormRow
            controlId="scopeType"
            label={t(tk.recommendedPublisher.scope)}
          >
            <Select
              options={scopeOpts}
              value={scopeOpts.find(o => o.value === scopeType)}
              onChange={opt => onScopeChange(opt?.value || null)}
              placeholder={t(tk.component.pointOut)}
              isClearable
              isDisabled={!profileId}
            />
          </FormRow>
          {showCompany && (
            <FormRow controlId="company" label={t(tk.common.company)}>
              <CompanySelector
                value={companyId || ''}
                onChange={opt => setCompanyId((opt as Option)?.value || null)}
                readOnly={false}
              />
            </FormRow>
          )}
          {scopeType === RecommendedPublisherScopeEnum.Purchaser && (
            <FormRow controlId="purchaser" label={t(tk.common.purchaser)}>
              <PurchaserSelector
                value={purchaserId || ''}
                onChange={opt => {
                  setPurchaserId((opt as Option)?.value || null);
                }}
                readOnly={!companyId}
                companyId={companyId || undefined}
                role={PermissionsEnum.Administration}
              />
            </FormRow>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={props.handleClose}>
          {t(tk.button.cancel)}
        </Button>
        <Button variant="primary" onClick={proceed} disabled={!isValid}>
          {t(tk.button.next)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
